import { formatTicker } from '@api';
import {
  CustomMixDomain,
  hasOHLC,
  RangeHorizon,
  ResampleIntervals,
  SeriesType,
  YAxisType,
} from '@chart';
import {
  type ChartV2AreaGradientStop,
  type ChartV2Handle,
  type ChartV2Horizon,
  type ChartV2Interval,
  type ChartV2Point,
  type ChartV2PriceDisplay,
  type ChartV2Series,
  type ChartV2SeriesType,
} from '@chartv2';
import {
  getChartV2DefaultPriceDisplayByPane,
  getChartV2PaneId,
  getChartV2YAxisLabel,
  getChartV2SeriesId as getSharedChartV2SeriesId,
  isPriceSnake,
  isYieldSnake,
  toChartV2Interval as mapToChartV2Interval,
  normalizeChartV2FallbackOhlcPoint,
  resolveChartV2Domain,
  toChartV2CompactHorizon,
  toChartV2RangeHorizon,
  toChartV2RenderableSeriesType,
  toChartV2Resample,
} from '@helpers';
import { AssetTickerLabel } from '@terminal/views/chart-v2/components/AssetTickerLabel';
import { SeriesLegendLabel } from '@terminal/views/chart-v2/components/SeriesLegendLabel';
import { SeriesLegendStatus } from '@terminal/views/chart-v2/components/SeriesLegendStatus';
import type { ChartV2SyntheticBars } from '@terminal/views/chart-v2/hooks/useChartV2LiveTicks';
import type {
  TerminalChartAssetData,
  TerminalChartEntity,
  TerminalPaneData,
} from '@terminal/views/turbo-chart/utils/chart-utils/types';
import { createElement } from 'react';

export interface TerminalPanesToSeriesOptions {
  /** Called when the user clicks the ticker/logo — typically opens the "change data" search modal. */
  onChangeTicker?: (entity: TerminalChartEntity, paneIndex: number) => void;
  /** Called when the user clicks the indicator name — typically opens the indicator picker. */
  onChangeIndicator?: (entity: TerminalChartEntity) => void;
  /** Tooltip label for the ticker button (e.g. `t('chart:changeData')`). */
  changeDataLabel?: string;
  /** Tooltip label for the indicator button (e.g. `t('chart:changeIndicator')`). */
  changeIndicatorLabel?: string;
  /** Synthetic live bars keyed by `subscribable_ticker`, produced by {@link useChartV2LiveTicks}. */
  liveTicks?: ChartV2SyntheticBars;
  /**
   * When true, each series' `color` is set to `var(${lineColorToken})`
   * instead of leaving it unset for chartv2's palette assign.
   *
   * That string is still a CSS var — ChartV2 copies it to `colorKey` for
   * legend/palette matching, then resolves it to a paintable hex/rgb on
   * `ChartV2ResolvedSeries.color` via `resolveCssVarColor` before the
   * canvas draws.
   */
  shouldUseLineColorToken?: boolean;
  /**
   * Vertical gradient fill drawn beneath each line series (matches the
   * simple chart's legacy `lineGradient`). Omit to leave lines unfilled.
   */
  areaGradient?: ChartV2AreaGradientStop[];
  /**
   * Enables the dashed latest-price reference line + right-side price pill
   * for price / yield series.
   *
   * @default true
   */
  showLatestPrice?: boolean;
}

interface AssetToSeriesParams {
  rawAsset: TerminalChartAssetData;
  paneIndex: number;
  paneSeriesType: SeriesType | undefined;
  isPrimary: boolean;
  options: TerminalPanesToSeriesOptions;
}

const isPriceOrYieldSnake = (snake: string | undefined): boolean =>
  !!snake && (isPriceSnake(snake) || isYieldSnake(snake));

/**
 * Append the accumulated synthetic bars (one per minute of elapsed live
 * ticks) to the tail of an asset's `ts`. All bar accumulation logic —
 * rolling over to a new bar every interval, extending high/low, resetting
 * when historical data catches up — lives in {@link useChartV2LiveTicks}.
 * We just splice the result in here so both the chart line and the
 * legend's `ts[-1].close` read see the same data.
 */
const applyLiveTick = (
  asset: TerminalChartAssetData,
  liveTicks: ChartV2SyntheticBars | undefined
): TerminalChartAssetData => {
  if (!isPriceOrYieldSnake(asset.snakeMeta?.snake)) {
    return asset;
  }

  const subscribable = asset.entity.subscribable_ticker;
  const bars = subscribable ? liveTicks?.[subscribable] : undefined;

  if (!bars || bars.length === 0) {
    return asset;
  }

  return {
    ...asset,
    ts: [...asset.ts, ...bars],
  };
};

export const toChartV2Horizon = (
  horizon: RangeHorizon | null | undefined
): ChartV2Horizon | undefined =>
  toChartV2CompactHorizon(horizon) as ChartV2Horizon | undefined;

export const toRangeHorizon = (horizon: ChartV2Horizon): RangeHorizon =>
  toChartV2RangeHorizon(horizon) as RangeHorizon;

export const toChartV2Interval = (
  resample: ResampleIntervals
): ChartV2Interval => mapToChartV2Interval(resample) as ChartV2Interval;

export const toResampleInterval = (
  interval: ChartV2Interval
): ResampleIntervals => toChartV2Resample(interval) as ResampleIntervals;

const toChartV2SeriesType = (type: SeriesType | undefined): ChartV2SeriesType =>
  toChartV2RenderableSeriesType(type) as ChartV2SeriesType;

/**
 * Inverse of {@link toChartV2SeriesType}. The terminal has no `area` type, so
 * it collapses to `line` — both are non-OHLC, which is the only distinction
 * the legend price draws from it. ChartV2's own per-series override still
 * renders the area fill.
 */
export const toTerminalSeriesType = (type: ChartV2SeriesType): SeriesType => {
  if (type === 'bar') {
    return 'bars';
  }

  return type === 'candlestick' ? 'candlestick' : 'line';
};

export const toTerminalYAxisType = (display: ChartV2PriceDisplay): YAxisType =>
  display === 'indexed' ? 'merged' : 'split';

export const getChartV2SeriesId = (asset: TerminalChartAssetData): string =>
  getSharedChartV2SeriesId({
    tag: asset.entity.tag,
    snake: asset.snakeMeta?.snake,
  });

const parseTsTime = (time: string): number => {
  const ms = Date.parse(time);

  return Number.isNaN(ms) ? 0 : ms;
};

export const toChartV2Domain = (
  domain: CustomMixDomain | null | undefined,
  primaryAsset: TerminalChartAssetData | undefined
): Parameters<ChartV2Handle['setDomain']>[0] | null => {
  if (!primaryAsset) {
    return null;
  }

  return resolveChartV2Domain({
    domain,
    points: primaryAsset.ts,
    getTime: point => point.time,
  });
};

/**
 * ChartV2 UTC-ms domain → URL / chart-state `CustomMixDomain` (ISO strings).
 *
 * Rounded to whole ms first: pan/zoom produce fractional timestamps, and
 * `toISOString` truncates them, so an unrounded value never round-trips
 * back to the number it came from.
 */
export const toTerminalDomain = (domain: {
  start: number;
  end: number;
}): CustomMixDomain => [
  new Date(Math.round(domain.start)).toISOString(),
  new Date(Math.round(domain.end)).toISOString(),
];

const toChartV2Point = (
  item: TerminalChartAssetData['ts'][number],
  seriesType: SeriesType | undefined
): ChartV2Point =>
  normalizeChartV2FallbackOhlcPoint(
    {
      time: parseTsTime(item.time),
      open: item.open,
      high: item.high,
      low: item.low,
      close: item.close,
    },
    seriesType
  ) as ChartV2Point;

/**
 * Y-axis column header. Mirrors the original chart's `AxisCurrency`:
 *   • `%` when the snake is a percentage series.
 *   • Uppercase currency ISO code (e.g. `"USD"`, `"GBP"`) for
 *     currency-denominated series.
 *   • Empty for everything else — the original filters ratio /
 *     count / boolean indicators out of the y-axis label entirely
 *     (a long indicator name like "Net Debt / EBITDA" overflows the
 *     pill and clips into adjacent columns).
 */
const getYAxisUnit = (asset: TerminalChartAssetData): string => {
  const { snakeMeta, entity } = asset;

  return getChartV2YAxisLabel({
    displayFormat: snakeMeta?.display_format,
    snakeCurrency: snakeMeta?.currency,
    entityCurrency: entity.currency,
  });
};

/**
 * The original chart gates the market-hours icons (WavePulse / CalendarClock
 * / SunRise / etc) behind `isPrimary={hasOHLC(primaryAsset)}` — the flag is
 * applied to every legend row, not just the first one. Mirroring that here
 * means every asset in an OHLC-driven chart (stock prices, etc) gets its
 * market-status icon, regardless of pane position.
 */
const assetToSeries = ({
  rawAsset,
  paneIndex,
  paneSeriesType,
  isPrimary,
  options,
}: AssetToSeriesParams): ChartV2Series => {
  const asset = applyLiveTick(rawAsset, options.liveTicks);
  const entity = asset.entity;
  const label = entity.formattedTicker || formatTicker(entity) || entity.name;
  const effectiveSeriesType = asset.seriesType ?? paneSeriesType;
  const data = asset.ts.map(point =>
    toChartV2Point(point, effectiveSeriesType)
  );
  // Use the turbo-chart palette token the terminal already assigned to
  // this asset so the legend's price text reads in the same hue as the
  // chart line. Close enough to chartv2's internal palette choice (both
  // pick from the same `--chart-multi-lines-hex-*` tokens).
  const legendColor = asset.lineColorToken
    ? `var(${asset.lineColorToken})`
    : 'var(--text-default)';
  // Only enable the horizontal "latest price" reference line on price /
  // yield series — for ratios, recommendations, indicators, etc. the
  // current value isn't meaningful as a standing reference.
  const hasLivePriceOverlay =
    options.showLatestPrice !== false &&
    isPriceOrYieldSnake(asset.snakeMeta?.snake);
  const color =
    options.shouldUseLineColorToken && asset.lineColorToken
      ? `var(${asset.lineColorToken})`
      : undefined;

  return {
    id: getChartV2SeriesId(asset),
    label,
    color,
    areaGradient: options.areaGradient,
    // Always emit an explicit string (even empty) so the canvas doesn't
    // fall back to the ticker label for ratios/indicators.
    yAxisLabel: getYAxisUnit(asset),
    showLatestPrice: hasLivePriceOverlay,
    legendLabel: createElement(SeriesLegendLabel, {
      entity,
      snakeMeta: asset.snakeMeta,
      changeDataLabel: options.changeDataLabel,
      changeIndicatorLabel: options.changeIndicatorLabel,
      onTickerClick: options.onChangeTicker
        ? () => options.onChangeTicker?.(entity, paneIndex)
        : undefined,
      onIndicatorClick: options.onChangeIndicator
        ? () => options.onChangeIndicator?.(entity)
        : undefined,
    }),
    chartTypeLabel: createElement(AssetTickerLabel, { entity }),
    legendStatus: createElement(SeriesLegendStatus, {
      asset,
      isPrimary,
      legendColor,
    }),
    seriesType: toChartV2SeriesType(effectiveSeriesType),
    data,
    paneId: getChartV2PaneId(paneIndex),
  };
};

export const terminalPanesToSeries = (
  panes: TerminalPaneData[],
  options: TerminalPanesToSeriesOptions = {}
): ChartV2Series[] => {
  const primaryAsset = panes[0]?.chartAssetData[0];
  const isOhlcChart = hasOHLC(primaryAsset);

  return panes.flatMap((pane, paneIndex) =>
    pane.chartAssetData.map(asset =>
      assetToSeries({
        rawAsset: asset,
        paneIndex,
        paneSeriesType: pane.seriesType,
        isPrimary: isOhlcChart,
        options,
      })
    )
  );
};

export const terminalPanesToDefaultHiddenSeriesIds = (
  panes: TerminalPaneData[]
): string[] =>
  panes.flatMap(pane =>
    pane.chartAssetData
      .filter(asset => asset.isHidden)
      .map(asset => getChartV2SeriesId(asset))
  );

export const terminalPanesToDefaultPriceDisplayByPane = (
  panes: TerminalPaneData[]
): Record<string, ChartV2PriceDisplay> =>
  getChartV2DefaultPriceDisplayByPane(
    panes.map((pane, paneIndex) => ({
      paneIndex,
      yAxisType: pane.yAxisType,
    }))
  ) as Record<string, ChartV2PriceDisplay>;
